/**
 * Skeleton template for executive-view. PLAN.md 3.4, 3.16.
 *
 * The skeleton is a normal Template in which body prose is redacted grapheme
 * by grapheme. Because it is the same type, the SAME engine runs on it in the
 * browser — sentence dropping, clause selection, numbering, plurals, party
 * repetition — with no server round-trip and no second implementation to
 * drift out of sync. Selecting a clause re-arranges the skeleton locally.
 *
 * Kept in clear:
 *   - headings (so the executive can confirm it is the right document)
 *   - every clause option, with its label (the executive must read what they choose)
 *   - field labels and enum options (the form needs them)
 * Redacted: all other body text, table cells, party wording, plural words.
 */
import {
  REDACTED,
  type Block, type ContentBlock, type InlineRun, type Sentence, type SentenceRun, type Template,
} from '@saledeed/shared';

const graphemes = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
const WORDISH = /[\p{L}\p{N}\p{M}]/u;

/** Letters and digits become REDACTED, one per grapheme; spaces and punctuation stay. */
export function redactText(text: string): string {
  let out = '';
  for (const { segment } of graphemes.segment(text)) out += WORDISH.test(segment) ? REDACTED : segment;
  return out;
}

function inline(run: InlineRun): InlineRun {
  switch (run.kind) {
    case 'text': return { ...run, text: redactText(run.text) };
    case 'plural': return { ...run, one: redactText(run.one), many: redactText(run.many) };
    case 'placeholder':
    case 'partyRef':
      return run;
  }
}

function sentenceRun(run: SentenceRun): SentenceRun {
  return run.kind === 'fragment' ? { ...run, runs: run.runs.map(inline) } : inline(run);
}

function sentence(s: Sentence): Sentence {
  return { ...s, runs: s.runs.map(sentenceRun) };
}

function content(b: ContentBlock): ContentBlock {
  switch (b.type) {
    case 'heading': return b;
    case 'paragraph': return { ...b, sentences: b.sentences.map(sentence) };
    case 'list': return { ...b, items: b.items.map(it => ({ ...it, sentences: it.sentences.map(sentence) })) };
    case 'table': return {
      ...b,
      rows: b.rows.map(r => ({ ...r, cells: r.cells.map(c => ({ ...c, sentences: c.sentences.map(sentence) })) })),
    };
  }
}

function block(b: Block): Block {
  switch (b.type) {
    case 'clauseSection':
      return b; // options are read in full to choose between them
    case 'partyBlock':
      return { ...b, variants: Object.fromEntries(Object.entries(b.variants).map(([type, blocks]) => [type, blocks.map(content)])) };
    default:
      return content(b);
  }
}

export function toSkeleton(template: Template): Template {
  return { ...template, blocks: template.blocks.map(block) };
}

export function isRedacted(text: string): boolean {
  return text.includes(REDACTED);
}
